import React, {memo, useCallback} from 'react'
import cx from 'classnames'

/* Cosmos */
import Button from '@cosmos/button'

/* Components */
import CampaignBundleSkeleton from './CampaignBundleSkeleton'

/* Constants */
import {DAVINCI_EVENT_NAMES} from '../../../../modules/analytics/davinci/davinciConstants'

/* Utils */
import {formatPrice} from '../../../../modules/product/productDesktopUtils'
import {yieldToMain} from '../../../../utils/performance'
import DataSet from '../../../../utils/DataSet'

/* Actions */
import {actionCreators as analyticsActionCreators} from '../../../../redux/actions/analyticsActions'

/* Styles */
import s from './campaign-bundle.scss'

const CampaignBundle = memo(({
  className,
  sku,
  bundleCampaign,
  isGetCampaignsPdpDashboardPending,
  prepareProduct,
  userInfo,
  dispatch,
  pageTypeForDavinci,
  categoryId,
  categoryName,
  categories,
  listingId,
  merchantId,
  merchantName,
  productId,
  price
}) => {
  const bundleCampaignProductCard = (bundleCampaignProduct, isFirstProduct = false) => {
    let image = bundleCampaignProduct?.Media?.LinkFormat || bundleCampaignProduct?.imageUrl
    let name = bundleCampaignProduct?.ProductName || bundleCampaignProduct?.name
    let price = formatPrice(bundleCampaignProduct?.DiscountedPrice) || formatPrice(bundleCampaignProduct?.price)
    return (
      <a
        className={s.bundleCampaignProductCard}
        href={isFirstProduct ? window.location.href : bundleCampaignProduct.Url}>
        <div className={s.bundleCampaignProductImage}>
          <img
            src={image?.replace?.('{size}', '140-140')}
            alt={bundleCampaignProduct?.ProductName || bundleCampaignProduct?.name || ''}/>
        </div>
        <div className={s.bundleCampaignName}>
          {bundleCampaign?.[0]?.campaignName}
        </div>
        <div className={s.bundleCampaignProductInformationContainer}>
          <div className={s.bundleCampaignProductPrice}>
            {price}
          </div>
          <div className={s.bundleCampaignProductName}>
            {name}
          </div>
        </div>
      </a>
    )
  }

  const bundleAddToCartEvent = useCallback(async(products) => {
    if (products) {
      const productsArr = []

      // eslint-disable-next-line array-callback-return
      products?.map((item, index) => {
        productsArr.push({
          campaignId: bundleCampaign?.[index]?.campaignId,
          id: item?.productId,
          listingId: item?.listingId,
          mainProduct: index === 0,
          name: item?.name,
          position: index + 1,
          price: item?.price?.toString(),
          quantity: 1,
          sku: item?.sku,
          merchantId: item?.merchantId
        })
      })

      window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.ADD_TO_CART_TOGETHER_CLICK, {
        canonical_url: window?.utagData?.canonical_url,
        category: categories?.length > 0 ? {
          item: categoryId || '',
          name: categoryName || '',
        } : null,
        category_hierarchy: `${categoryId} - ${categoryName}`,
        category_status: '',
        experiments: [],
        listings: [],
        location: 'CampaignBundle',
        merchant: '',
        page_type: pageTypeForDavinci,
        page_value: sku,
        products: productsArr,
        site_type: 'desktop'
      })
    }
  })

  const addToCart = () => {
    if (window?.Checkout?.AddToCart) {
      let campaignBundleAddToCartData = [prepareProduct]

      bundleCampaign?.[0]?.awardProductList?.map(item => {
        const awardProduct = {}
        awardProduct.sku = item.SKU
        awardProduct.productId = item.ProductId
        awardProduct.listingId = item.ListingId
        awardProduct.quantity = 1
        awardProduct.imageUrl = item.Media.LinkFormat
        awardProduct.name = item.ProductName
        awardProduct.merchantId = item.MerchantId
        awardProduct.merchantName = item.MerchantName
        awardProduct.price = item.DiscountedPrice.toFixed(2).toLocaleString()
        campaignBundleAddToCartData.push(awardProduct)
      })

      bundleAddToCartEvent(campaignBundleAddToCartData)
      window?.Checkout?.AddToCart(campaignBundleAddToCartData, 'addToBasketBundles')

      campaignBundleAddToCartData?.forEach(product => {
        sendWebtrekkForAddToCart(product)
      })
    }
  }

  const sendWebtrekkForAddToCart = useCallback(async(product) => {
    const webtrekkData = {
      contentId: 'Mobile-SpeedCart-Product-TabKampanyalar',
      contentGroup: {
        1: 'Mobile',
        2: '_SpeedCart',
        3: 'Product Detail',
        4: window?.location?.href
      },
      customEcommerceParameter: {
        16: `ProductPage > ${window?.utagData?.category_name_hierarchy}`,
        17: 'CampaignBundle',
        18: product?.merchantName || '',
        19: product?.merchantId || ''
      },
      customSessionParameter: {
        4: userInfo?.isAuthenticated ? 'Login' : 'Not Login',
      },
      product: product?.sku || '',
      productStatus: 'add',
      productQuantity: '1',
      productCost: parseInt(product?.price) || ''
    }
    await yieldToMain()
    dispatch(
      analyticsActionCreators.sendWebtrekkEvent.trigger(webtrekkData)
    )
  })

  let productCount = bundleCampaign?.[0]?.awardProductList?.length > 0 ? `(${bundleCampaign?.[0]?.awardProductList?.length + 1})` : ''
  return (
    <div
      className={cx(s.root, className, {[s.noItem]: !isGetCampaignsPdpDashboardPending && (!bundleCampaign || bundleCampaign?.[0]?.awardProductList.length === 0)})}
      data-test-id={'campaign-bundle'}>
      {
        isGetCampaignsPdpDashboardPending ?
          <CampaignBundleSkeleton/> :
          <div
            className={s.container} data-hbus={DataSet.createComponentViewDataForDavinci({
              placement: 'CampaignBundle',
              component: 'CampaignBundle',
              componentDetail: 'Birlikte al',
              pageType: pageTypeForDavinci,
              pageValue: sku,
              productId: productId,
              merchantName: merchantName,
              merchantId: merchantId,
              listingId: listingId || '',
              price: `${price}` || ''
            })}>
            <div className={cx(s.content, s.contentLeft)}>
              <div className={s.staticText}>
                {'Birlikte Al, Kazan'}
              </div>
              <div className={s.infoBox}>
                <i className={s.icon} aria-hidden/>
                <div className={s.infoBoxDiscount}>{formatPrice(bundleCampaign?.[0]?.totalCampaignDiscount)}</div>
                <div className={s.infoBoxStaticText}>{'Kazanç'}</div>
              </div>
            </div>

            <div className={cx(s.content, s.contentMiddle)}>
              {bundleCampaignProductCard(prepareProduct, true)}
              {
                bundleCampaign?.[0]?.awardProductList.map((item, index) => {
                  return (
                    bundleCampaignProductCard(item)
                  )
                })
              }
            </div>
            <div className={cx(s.content, s.contentRight)}>
              <div className={s.discountContainer}>
                <div className={s.discountPriceContainer}>
                  <div className={s.staticDiscountTitle}>Toplam Kazancın</div>
                  <div
                    className={cx(s.discountPrice, s.badge, s.badgeGreen)}>{formatPrice(bundleCampaign?.[0]?.totalCampaignDiscount)}</div>
                </div>

                <div className={s.totalDiscountContainer}>
                  <div className={s.staticTotalDiscountTitle}>Ödeyeceğin Fiyat</div>
                  {
                    bundleCampaign?.[0]?.totalPrice ?
                      <div className={s.discountLineThrough}>
                        {formatPrice(bundleCampaign?.[0]?.totalPrice)}
                      </div>
                      : null
                  }
                  <div
                    className={cx(s.totalDiscountedPrice, s.badge, s.badgeGray)}>{formatPrice(bundleCampaign?.[0]?.totalDiscountedPrice)}</div>
                </div>
              </div>

              <Button
                type='button'
                name='btnAddAll'
                id='btnAddAll'
                size='small'
                className={s.button}
                onClick={() => addToCart()}>
                <i aria-hidden/>
                {`Birlikte sepete ekle ${productCount}`}
              </Button>
            </div>
          </div>
      }
    </div>
  )
})

export default CampaignBundle
